import { Spin } from 'antd';
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { Bar, BarChart, LabelList, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';

import theme from '../../../assets/theme';
import usePoll from '../../../hooks/usePoll';
import { RoundOptions } from '../../../services/backendService/types';
import { getIndexToColor } from '../../../utils/roundUtils';
import { CenteredContainer } from '../../styled/CenteredContainer';

const Poll = ({
  roundId,
  gameId,
  staticData,
  isAuthenticated,
  hideCount = false,
  tenantId,
}: {
  roundId?: string;
  gameId?: string;
  isAuthenticated: boolean;
  staticData?: RoundOptions[] | null;
  hideCount?: boolean;
  tenantId?: string;
}) => {
  const { data } = usePoll({ roundId, gameId, isAuthenticated, tenantId });
  // Recharts uses the BarChart id to generate internal <defs> ids
  // (clipPath, gradients). With multiple Poll charts on the same page
  // (e.g. the editor canvas plus slide-list thumbnails) a hardcoded id
  // collides and one chart's clip ends up pointed at another chart's
  // defs, blanking the bars. useId gives each mount a unique id —
  // mirrors the fix already applied in MultipleChoiceBars.
  const chartId = useId();

  const [presentableData, setPresentableData] = useState<RoundOptions[]>();

  useEffect(() => {
    if (data) {
      setPresentableData(data);
    }
  }, [data]);

  useEffect(() => {
    if (staticData) {
      setPresentableData(staticData);
    }
  }, [staticData]);

  // Per-bar palette color. Bar fill and the count label above each bar
  // both read from this array so the chart stays visually unified per
  // option. Falls back to the index palette so legacy poll data without
  // an explicit option color still renders something visible.
  const colorByIndex = useMemo(() => {
    if (!presentableData) return [] as string[];
    return presentableData.map(
      (opt, i) => opt.color || getIndexToColor(i) || theme.colors.black,
    );
  }, [presentableData]);

  // Memoize label font-size + the LabelList render function so the
  // reference is stable across renders. Recharts re-runs the content
  // renderer on every reference change, which previously fired on every
  // parent render even when nothing visible changed.
  const labelFontSize = useMemo(
    () => `${(1920 * 0.1) / (presentableData?.length || 1)}px`,
    [presentableData?.length],
  );

  const renderLabel = useCallback(
    (props: {
      index?: number;
      value?: number | string;
      x?: number | string;
      y?: number | string;
      width?: number | string;
    }) => {
      const { index, value, x, y, width } = props;
      if (index === undefined) return null;
      const numX = Number(x) || 0;
      const numY = Number(y) || 0;
      const numWidth = Number(width) || 0;
      return (
        <text
          x={numX + numWidth / 2}
          y={numY - 4}
          textAnchor="middle"
          fontWeight={800}
          fontSize={labelFontSize}
          fill={colorByIndex[index] ?? theme.colors.black}
        >
          {value}
        </text>
      );
    },
    [labelFontSize, colorByIndex],
  );

  if (!presentableData)
    return (
      <CenteredContainer style={{ height: '90%', width: `100%` }}>
        <Spin />
      </CenteredContainer>
    );
  if (presentableData.length == 0) return null;

  return (
    <Container width="100%" height="100%">
      <BarChart
        width={150}
        id={chartId}
        data={presentableData}
        margin={{ top: 0, right: 10, left: 10, bottom: 10 }}
      >
        <Bar
          dataKey="count"
          shape={<CuripodBar colors={colorByIndex} />}
          minPointSize={5}
        >
          {!hideCount && (
            <LabelList dataKey="count" position="top" content={renderLabel} />
          )}
        </Bar>
      </BarChart>
    </Container>
  );
};
const Container = styled(ResponsiveContainer)`
  svg {
    overflow: visible;
  }
  tspan {
    font-size: 100%;
    cursor: default;
  }
`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CuripodBar = (props: any) => {
  const { index, width, height, x, y, colors } = props;
  const fill = colors?.[index] ?? theme.colors.black;
  return (
    <rect
      x={x}
      y={y}
      width={width >= 0 ? width : 0}
      height={height >= 0 ? height : 0}
      rx="10"
      fill={fill}
      filter={'drop-shadow(rgba(0, 0, 0, 0.1) 0px -2px 3px)'}
    />
  );
};

export default Poll;
